// LabelDocument.js - Versão atualizada com suporte a água

import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Certifique-se de que este caminho aponta para o ficheiro real do seu logótipo
const LOGO_URL = '/assets/grupo_logo.png';

const styles = StyleSheet.create({
    page: {
        padding: 48,
        backgroundColor: '#ffffff'
    },
    container: {
        flex: 1,
        justifyContent: 'flex-start'
    },
    header: {
        alignItems: 'center',
        marginBottom: 40,
    },
    logo: {
        width: 280,
        height: 140,
        objectFit: 'contain'
    },
    content: {
        padding: '20px 0'
    },
    row: {
        marginBottom: 20,
        flexDirection: 'row',
        alignItems: 'flex-start'
    },
    label: {
        fontSize: 30,
        fontWeight: 'bold',
        letterSpacing: 1.5,
        width: 140
    },
    value: {
        fontSize: 30,
        fontWeight: 'bold',
        flex: 1,
        lineHeight: 1.4
    },
    dateRow: {
        marginTop: 50,
        paddingTop: 24,
        borderTopWidth: 1.5,
        borderTopColor: '#000000',
        borderTopStyle: 'solid',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    dateItem: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    dateLabel: {
        fontSize: 30,
        fontWeight: 'bold',
        letterSpacing: 1.5,
        marginRight: 10
    },
    dateValue: {
        fontSize: 30,
        fontWeight: 'bold'
    },
    // Novo estilo para água
    waterLabel: {
        fontSize: 30,
        fontWeight: 'bold',
        color: '#1565c0',
        textAlign: 'center',
        marginTop: 20,
        padding: 10,
        backgroundColor: '#e3f2fd',
        borderRadius: 4
    }
});

const formatDate = (dateValue) => {
    if (!dateValue) return '--/--/----';

    try {
        let d;

        if (dateValue && typeof dateValue.toDate === 'function') {
            d = dateValue.toDate();
        } else if (dateValue instanceof Date) {
            d = dateValue;
        } else if (typeof dateValue === 'string') {
            d = new Date(dateValue);
            if (isNaN(d.getTime())) {
                const parts = dateValue.split('/');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    d = new Date(year, month, day);
                }
            }
        } else if (typeof dateValue === 'number') {
            d = new Date(dateValue);
        } else {
            d = new Date(dateValue);
        }

        if (!d || isNaN(d.getTime())) {
            return '--/--/----';
        }

        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();

        return `${day}/${month}/${year}`;
    } catch (error) {
        return '--/--/----';
    }
};

// Helper para verificar se a data está expirada
const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    try {
        let expiry;

        if (expirationDate.toDate) {
            expiry = expirationDate.toDate();
        } else if (typeof expirationDate === 'string') {
            const parts = expirationDate.split('-');
            if (parts.length === 3) {
                expiry = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
            } else {
                expiry = new Date(expirationDate);
            }
        } else if (expirationDate instanceof Date) {
            expiry = expirationDate;
        } else {
            expiry = new Date(expirationDate);
        }

        if (isNaN(expiry.getTime())) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expiryDate = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
        return expiryDate < today;
    } catch {
        return false;
    }
};

const LabelDocument = ({ item }) => {
    const code = item?.rawMaterialCode || item?.code || '';
    const name = item?.rawMaterialName || item?.name || '';
    const lot = item?.lotNumber || item?.lot || '';
    const isWater = item?.isWater || false;

    const manufacturerDate = item?.manufacturerDate || item?.manufacturingDate || item?.manufactureDate || null;
    const expirationDate = item?.expirationDate || item?.validityDate || item?.expiryDate || null;
    const expiryStatus = item?.expiryStatus || null;

    // Verifica se está expirado e em análise
    const isExpiredAndPendingReview = isExpired(expirationDate) && expiryStatus === 'PENDING_REVIEW';

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.container}>
                    {/* Cabeçalho com o Logótipo */}
                    <View style={styles.header}>
                        <Image style={styles.logo} src={LOGO_URL} />
                    </View>

                    {/* Conteúdo dos Dados */}
                    <View style={styles.content}>
                        <View style={styles.row}>
                            <Text style={styles.label}>COD.:</Text>
                            <Text style={styles.value}>{code}</Text>
                        </View>

                        <View style={styles.row}>
                            <Text style={styles.label}>NOME:</Text>
                            <Text style={styles.value}>{name}</Text>
                        </View>

                        {isWater ? (
                            // Se for água, mostra mensagem especial
                            <>
                                <View style={styles.row}>
                                    <Text style={styles.label}>LOTE:</Text>
                                    <Text style={styles.value}>N/A (Recurso Infinito)</Text>
                                </View>
                                <Text style={styles.waterLabel}>♾️ RECURSO INFINITO - ÁGUA</Text>
                            </>
                        ) : (
                            // Para outros itens, mostra lote normal
                            <View style={styles.row}>
                                <Text style={styles.label}>LOTE:</Text>
                                <Text style={styles.value}>{lot}</Text>
                            </View>
                        )}

                        {/* Mensagem de aviso quando expirado e aguardando análise */}
                        {!isWater && isExpiredAndPendingReview && (
                            <View style={styles.warningMessage}>
                                <Text>⚠️ AGUARDANDO PARECER DO CONTROLE DE QUALIDADE ⚠️</Text>
                            </View>
                        )}

                        {/* Secção Inferior de Datas */}
                        <View style={styles.dateRow}>
                            <View style={styles.dateItem}>
                                <Text style={styles.dateLabel}>F:</Text>
                                <Text style={styles.dateValue}>
                                    {isWater ? '--/--/----' : formatDate(manufacturerDate)}
                                </Text>
                            </View>
                            <View style={styles.dateItem}>
                                <Text style={styles.dateLabel}>V:</Text>
                                <Text style={styles.dateValue}>
                                    {isWater ? '--/--/----' : formatDate(expirationDate)}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default LabelDocument;